<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    :disabled="isDisabled"
    :hide-reset="!!rideInfo"
    @submit="sendTextDriver"
    @reset="resetForm"
  >
    <template #title>
      Text the Driver
    </template>

    <template #card-notes>
      <div class="card-notes">
        <p>This sends a text immediately from GoGo's text number. It will appear in the activity log.</p>
      </div>
    </template>

    <template #content>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Name*</label>
        <div class="col-sm-7">
          <sanitized-input
            id="customerName"
            v-model="requestBody.customerName"
            placeholder="Robert Wenzel"
            required
            :disabled="!!rideInfo"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Registered Number*</label>
        <phone-number-input
          :id="!!rideInfo ? `registered-phone-${rideInfo.id}` : 'registered-phone'"
          v-model="requestBody.customerRegisteredNumber"
          class="col-sm-7"
          :page-id="pageId"
          :disabled="!!rideInfo"
        />
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Operator Name*</label>
        <div class="col-sm-7">
          <sanitized-input
            id="operatorName"
            v-model="requestBody.operatorName"
            placeholder="James Downey"
            required
            :disabled="!!rideInfo"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Ride ID*</label>
        <div class="col-sm-7">
          <input
            id="rideId"
            v-model.number="requestBody.rideId"
            type="number"
            class="form-control"
            placeholder="Ride ID"
            min="1"
            step="1"
            required
            :disabled="!!rideInfo"
          >
        </div>
      </div>

      <SmsConversationHistory
        :messages="messages"
        :is-loading="isLoadingHistory"
        :is-phone-number-valid="canLoadHistory"
        empty-invalid-message="Enter a ride ID to load message history."
      />

      <div class="row">
        <label class="col-sm-3 control-label pt-5 mobile-pt-0"> Text Content* </label>
        <div class="col-sm-7">
          <sanitized-textarea
            id="txtContent"
            v-model="requestBody.textContent"
            placeholder="The customer is waiting you to go to shopping."
            autosize
            required
          />
        </div>
      </div>
    </template>

    <template #submit-button>
      Send Text
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { computed, ref, Ref, watch, onMounted } from 'vue';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useProfile } from '@/use/profile';
import { useSwalDialog } from '@/use/swalDialog';
import { useSmsConversationHistory } from '@/use/smsConversationHistory';
import { textMessageService } from '@/services/textMessage';
import { SmsRecipientName } from '@/gql/graphql';
import SmsConversationHistory from './SmsConversationHistory.vue';

const pageId = 'text-the-driver';

const props = defineProps({
  rideInfo: {
    type: Object,
    default: () => null,
  },
});

const { customerInfo, customerPhoneNumber, lastRideInfo } = useCustomer();
const { userFullName } = useProfile();
const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

const requestBody: Ref<any> = ref({
  customerName: '',
  customerRegisteredNumber: '',
  operatorName: '',
  rideId: null as number | null,
  textContent: '',
});
const isLoading = ref(false);

const parseRideId = (value: unknown): number | null => {
  const rideId = Number(value);
  if (!Number.isInteger(rideId) || rideId <= 0) {
    return null;
  }
  return rideId;
};

const isCustomerPhoneNumberValid = computed(() => util.isPhoneNumberValid(requestBody.value.customerRegisteredNumber));

const rideId = computed(() => parseRideId(requestBody.value.rideId));
const { messages, isLoadingHistory, isPhoneNumberValid: canLoadHistory, loadMessageHistory } =
  useSmsConversationHistory(undefined, { rideId });

const isDisabled = computed(
  () =>
    isLoading.value ||
    (!isCustomerPhoneNumberValid.value && !props.rideInfo) ||
    rideId.value == null ||
    !requestBody.value.textContent.trim(),
);

const sendTextDriver = async () => {
  const parsedRideId = parseRideId(requestBody.value.rideId);
  if (parsedRideId == null) {
    showErrorPopupDialog({
      text: 'Please enter a valid Ride ID.',
    });
    return;
  }

  isLoading.value = true;
  try {
    await textMessageService.sendOperatorTextMessage({
      rideId: parsedRideId,
      message: requestBody.value.textContent.trim(),
      recipientType: SmsRecipientName.Driver,
      associatedCallerId: customerInfo.value?.id,
      customerName: requestBody.value.customerName,
      operatorName: requestBody.value.operatorName,
      customerRegisteredNumber: requestBody.value.customerRegisteredNumber,
    });

    requestBody.value.textContent = '';
    await loadMessageHistory();
    showSuccessPopupDialog({
      text: 'Text sent successfully.',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  isLoading.value = false;
};

const resetForm = () => {
  requestBody.value = {
    customerName: '',
    customerRegisteredNumber: '',
    operatorName: userFullName.value,
    rideId: null,
    textContent: '',
  };
};

watch(lastRideInfo, (value: any) => {
  if (props.rideInfo) {
    return;
  }

  requestBody.value.rideId = parseRideId(value?.id);
});

watch(customerInfo, (value: any) => {
  if (props.rideInfo || !value.id) {
    return;
  }

  resetForm();

  requestBody.value.customerName = util.getFullName(value.firstName, value.lastName);
  requestBody.value.operatorName = userFullName.value;
  requestBody.value.customerRegisteredNumber = customerPhoneNumber.value;
});

onMounted(() => {
  if (!props.rideInfo) {
    // prefill the form with custome rinfo
    if (customerInfo.value.id) {
      requestBody.value.customerName = util.getFullName(customerInfo.value.firstName, customerInfo.value.lastName);
    }

    requestBody.value.customerRegisteredNumber = customerPhoneNumber.value || requestBody.value.customerRegisteredNumber;
    if (lastRideInfo.value?.id) {
      requestBody.value.rideId = parseRideId(lastRideInfo.value.id);
      requestBody.value.customerRegisteredNumber = lastRideInfo.value.riderPhone;
    }
  } else {
    requestBody.value.rideId = parseRideId(props.rideInfo.id);
    requestBody.value.customerRegisteredNumber = props.rideInfo.riderPhone;
    requestBody.value.customerName = util.getFullName(props.rideInfo.firstName, props.rideInfo.lastName);
  }

  requestBody.value.operatorName = userFullName.value;
});
</script>
